import React, { useState } from 'react';
import { Card } from '../UI';
import { fmt } from '../../utils';
import { DETAIL_COLUMNS, distinctLabel, groupModeLabel, STATUS_STYLES, fmtCount } from './erUtils';

const NAVY = '#0b318b';

const summaryTh = {
  padding: '11px 12px', textAlign: 'left', fontSize: 10, fontWeight: 800,
  color: '#fff', textTransform: 'uppercase', letterSpacing: '0.07em',
  whiteSpace: 'nowrap', background: NAVY, borderRight: '1px solid rgba(255,255,255,0.15)',
};
const summaryTd = {
  padding: '11px 12px', fontSize: 12.5, borderRight: '1px solid #e2e8f0',
  borderBottom: '1px solid #e2e8f0', verticalAlign: 'middle',
};
const detailTh = {
  padding: '8px 10px', textAlign: 'left', fontSize: 9.5, fontWeight: 800,
  color: '#334155', textTransform: 'uppercase', letterSpacing: '0.05em',
  whiteSpace: 'nowrap', background: '#e2e8f0', borderRight: '1px solid #cbd5e1',
  position: 'sticky', top: 0,
};
const detailTd = {
  padding: '7px 10px', fontSize: 11.5, whiteSpace: 'nowrap',
  borderRight: '1px solid #eef2f6', borderBottom: '1px solid #eef2f6',
};

function StatusPill({ value }) {
  const s = STATUS_STYLES[value] || { bg: '#f1f5f9', color: '#475569', label: value };
  return (
    <span style={{
      background: s.bg, color: s.color, padding: '2px 9px', borderRadius: 999,
      fontSize: 10.5, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.03em',
    }}>{s.label}</span>
  );
}

function DetailTable({ rows }) {
  return (
    <div style={{ overflowX: 'auto', maxHeight: 460, overflowY: 'auto', background: '#fbfdff', borderTop: '2px solid #cbd5e1' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th style={{ ...detailTh, left: 0, zIndex: 2 }}>#</th>
            {DETAIL_COLUMNS.map(c => (
              <th key={c.key} style={{ ...detailTh, textAlign: c.align || 'left' }}>{c.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((line, i) => (
            <tr key={line.line_id} style={{ background: i % 2 ? '#f8fafc' : '#fff' }}>
              <td style={{ ...detailTd, fontWeight: 700, color: '#64748b' }}>{i + 1}</td>
              {DETAIL_COLUMNS.map(c => {
                const raw = line[c.key];
                let content;
                if (c.isStatus) content = <StatusPill value={raw} />;
                else if (c.render) content = raw === null || raw === undefined ? '—' : c.render(raw);
                else content = raw || '—';
                return (
                  <td
                    key={c.key}
                    title={c.wide && raw ? String(raw) : undefined}
                    style={{
                      ...detailTd,
                      textAlign: c.align || 'left',
                      fontWeight: c.bold ? 700 : 400,
                      fontFamily: c.mono ? 'var(--mono)' : undefined,
                      maxWidth: c.wide ? 240 : undefined,
                      overflow: c.wide ? 'hidden' : undefined,
                      textOverflow: c.wide ? 'ellipsis' : undefined,
                    }}
                  >
                    {content}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function ExpenseRegisterTable({ groups, groupBy, grandTotal }) {
  const [expanded, setExpanded] = useState(() => new Set());

  const toggle = (key) => setExpanded(prev => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  const groupNoun = groupModeLabel(groupBy);

  if (groups.length === 0) {
    return (
      <Card style={{ border: '2px solid black', borderRadius: 16, padding: 48, textAlign: 'center', color: 'var(--text-muted)', boxShadow: '0 4px 20px -2px rgba(15, 23, 42, 0.04)' }}>
        No expense lines match the current filters.
      </Card>
    );
  }

  return (
    <Card style={{ border: '2px solid black', borderRadius: 16, padding: 0, overflow: 'hidden', boxShadow: '0 4px 20px -2px rgba(15, 23, 42, 0.04)' }}>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={{ ...summaryTh, width: 40 }} />
              <th style={{ ...summaryTh, width: 54 }}>S/N</th>
              <th style={summaryTh}>{groupNoun}</th>
              <th style={summaryTh}>Trans Line</th>
              <th style={summaryTh}>Voucher Ref</th>
              <th style={summaryTh}>Req Ref</th>
              <th style={summaryTh}>Description</th>
              <th style={{ ...summaryTh, textAlign: 'right' }}>Total Amt</th>
              <th style={{ ...summaryTh, textAlign: 'right' }}>Amt Payable</th>
              {/* Amt Paid — hidden on request. Uncomment this, its <td> below,
                  and restore colSpan to 13 to bring the column back. */}
              {/* <th style={{ ...summaryTh, textAlign: 'right' }}>Amt Paid</th> */}
              <th style={summaryTh}>Department</th>
              <th style={summaryTh}>Project</th>
              <th style={summaryTh}>Vendor</th>
            </tr>
          </thead>
          <tbody>
            {groups.map((g, idx) => {
              const isOpen = expanded.has(g.key);
              return (
                <React.Fragment key={g.key}>
                  <tr
                    onClick={() => toggle(g.key)}
                    style={{ cursor: 'pointer', background: isOpen ? '#eff6ff' : '#fff' }}
                    onMouseEnter={e => { if (!isOpen) e.currentTarget.style.background = '#f8fafc'; }}
                    onMouseLeave={e => { if (!isOpen) e.currentTarget.style.background = '#fff'; }}
                  >
                    <td style={{ ...summaryTd, textAlign: 'center', color: NAVY, fontWeight: 800 }}>
                      {isOpen ? '▾' : '▸'}
                    </td>
                    <td style={{ ...summaryTd, textAlign: 'center', fontWeight: 700, color: '#475569' }}>{idx + 1}</td>
                    <td style={{ ...summaryTd, fontWeight: 800, color: NAVY }}>{g.key}</td>
                    <td style={summaryTd}>{fmtCount(g.lineCount)} Line{g.lineCount === 1 ? '' : 's'}</td>
                    <td style={summaryTd}>{distinctLabel(g.vouchers, 'Voucher')}</td>
                    <td style={summaryTd}>{distinctLabel(g.requisitions, 'Requisition')}</td>
                    <td style={{ ...summaryTd, maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {g.sampleDescription}
                    </td>
                    <td style={{ ...summaryTd, textAlign: 'right', fontWeight: 800 }}>{fmt(g.totalAmount)}</td>
                    <td style={{ ...summaryTd, textAlign: 'right', fontWeight: 700, color: '#1d4ed8' }}>{fmt(g.totalPayable)}</td>
                    {/* Amt Paid — hidden on request, see the header above. */}
                    {/* <td style={{ ...summaryTd, textAlign: 'right', fontWeight: 700, color: '#16a34a' }}>{fmt(g.totalPaid)}</td> */}
                    <td style={summaryTd}>{distinctLabel(g.departments, 'Department')}</td>
                    <td style={summaryTd}>{distinctLabel(g.projects, 'Project')}</td>
                    <td style={summaryTd}>{distinctLabel(g.vendors, 'Vendor')}</td>
                  </tr>
                  {isOpen && (
                    <tr>
                      {/* 13 while Amt Paid is shown — see the commented column above. */}
                      <td colSpan={12} style={{ padding: 0 }}>
                        <DetailTable rows={g.rows} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '18px 24px', background: NAVY, color: '#fff', flexWrap: 'wrap', gap: 10,
      }}>
        <span style={{ fontSize: 13, fontWeight: 800, letterSpacing: 0.6 }}>
          GRAND TOTAL — ALL EXPENSES
        </span>
        <div style={{ display: 'flex', gap: 28, alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 10, opacity: 0.75, fontWeight: 700, letterSpacing: 0.5 }}>PAYABLE</div>
            <div style={{ fontSize: 15, fontWeight: 700, fontFamily: 'var(--mono)' }}>{fmt(grandTotal.payable)}</div>
          </div>
          {/* PAID — hidden on request, with the Amt Paid column above.
              Uncomment to bring the grand total back. */}
          {/* <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 10, opacity: 0.75, fontWeight: 700, letterSpacing: 0.5 }}>PAID</div>
            <div style={{ fontSize: 15, fontWeight: 700, fontFamily: 'var(--mono)' }}>{fmt(grandTotal.paid)}</div>
          </div> */}
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 10, opacity: 0.75, fontWeight: 700, letterSpacing: 0.5 }}>TOTAL AMOUNT</div>
            <div style={{ fontSize: 24, fontWeight: 800, fontFamily: 'var(--mono)' }}>{fmt(grandTotal.amount)}</div>
          </div>
        </div>
      </div>
    </Card>
  );
}
